import { useEffect, useRef, useState } from "react";

const EARLY_TICKS = [1, 3, 5, 7];

const STATUS_ALIASES = {
  identifying_brand: "finding_competitors",
  searching_meta: "searching",
  searching_google: "searching",
  searching_linkedin: "searching",
};

export const AI_COMPETITORS_ANALYSIS_STAGES = [
  { status: "analyzing_website", start: 1, cap: 32 },
  { status: "finding_competitors", start: 34, cap: 65 },
  { status: "preparing_competitors", start: 67, cap: 96 },
];

export const AI_COMPETITORS_ADS_SEARCH_STAGES = [
  { status: "resolving", start: 1, cap: 10 },
  { status: "searching", start: 11, cap: 85, pace: "slow" },
  { status: "preparing", start: 86, cap: 100 },
];

const resolveStatus = (status, stages) => {
  const aliased = STATUS_ALIASES[status] || status;
  if (stages.some((stage) => stage.status === aliased)) {
    return aliased;
  }
  return stages[0]?.status || "";
};

const getStage = (stages, status) =>
  stages.find((stage) => stage.status === status) || stages[0];

export const nextAiCompetitorsProgressTick = (current, cap, pace = "default") => {
  const softCap = cap - 1;
  const value = Number(current) || 0;
  if (value >= softCap) return value;
  if (pace !== "slow" && value < 7) {
    const nextEarly = EARLY_TICKS.find((tick) => tick > value && tick <= softCap);
    if (nextEarly) return nextEarly;
  }
  const remaining = softCap - value;
  if (pace === "slow") {
    const step =
      remaining > 24 ? 1.2 : remaining > 12 ? 0.7 : remaining > 4 ? 0.35 : 0.15;
    return Math.min(value + step, softCap);
  }
  const step = remaining > 18 ? 3 : remaining > 8 ? 2 : remaining > 2 ? 1 : 0.4;
  return Math.min(value + step, softCap);
};

export const aiCompetitorsProgressDelayMs = (current, start, cap, pace = "default") => {
  const softCap = Math.max(cap - 1, start);
  const span = Math.max(softCap - start, 1);
  const progressed = Math.max(Number(current) || 0, start) - start;
  const closeness = Math.min(progressed / span, 1);
  if (pace === "slow") {
    return Math.round(560 + closeness * closeness * 3200);
  }
  return Math.round(280 + closeness * closeness * 1700);
};

export const useAiCompetitorsStageProgress = ({
  stages,
  status,
  copy,
  backendProgress,
  fallbackCopyByStatus = {},
  isActive = false,
}) => {
  const resolvedStatus = isActive ? resolveStatus(status, stages) : "";
  const stage = getStage(stages, resolvedStatus);
  const fallbackCopy =
    fallbackCopyByStatus[resolvedStatus] ||
    fallbackCopyByStatus[stages[0]?.status] ||
    "";
  const liveCopy =
    isActive && status && status !== "idle" && status !== "done"
      ? copy
      : "";
  const [progress, setProgress] = useState(1);
  const [label, setLabel] = useState(fallbackCopy);
  const progressRef = useRef(1);

  useEffect(() => {
    if (!isActive) {
      progressRef.current = 1;
      setProgress(1);
      return undefined;
    }

    setLabel(liveCopy || fallbackCopy);
    const hasLiveStatus = Boolean(status) && status !== "idle";
    const floor = Math.min(
      stage.cap - 1,
      Math.max(
        stage.start,
        hasLiveStatus && Number(backendProgress) > 0
          ? Number(backendProgress)
          : 0
      )
    );
    setProgress((current) => {
      const next = Math.max(current, floor);
      progressRef.current = next;
      return next;
    });
    return undefined;
  }, [
    backendProgress,
    fallbackCopy,
    isActive,
    liveCopy,
    stage.cap,
    stage.start,
    status,
  ]);

  useEffect(() => {
    if (!isActive) return undefined;

    let timeoutId = 0;
    const scheduleTick = () => {
      const delay = aiCompetitorsProgressDelayMs(
        progressRef.current,
        stage.start,
        stage.cap,
        stage.pace
      );
      timeoutId = window.setTimeout(() => {
        setProgress((current) => {
          const next = nextAiCompetitorsProgressTick(
            current,
            stage.cap,
            stage.pace
          );
          progressRef.current = next;
          return next;
        });
        scheduleTick();
      }, delay);
    };
    scheduleTick();

    return () => window.clearTimeout(timeoutId);
  }, [isActive, resolvedStatus, stage.cap, stage.pace, stage.start]);

  return {
    progress: isActive ? progress : 1,
    label: liveCopy || fallbackCopy || label,
  };
};
